import React, {useEffect, useState, useRef} from "react";
import {Spinner, Row, Col} from "react-bootstrap";
import moment from 'moment';
import t from "../../i18n/translate";
// Material-UI Icons for custom navigation
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
// Assuming API is correctly imported from its path in a real project
import API from "../../network/redAPI"
import {
	StyledNavLinkButton,
	NoSlotsMessage,
	SlotsInnerContainer,
	SlotsGridRow,
	SlotColumn,
	PageContainer,
	LoadingContainer,
	StyledAlert,
	DateNavWrapper,
	DateNavScrollContainer,
	NavArrowButton
} from "./StyleSheet"
import SlotButton from "./SlotButton";
import {Typography} from "@material-ui/core"; // Assuming SlotButton is a custom component for rendering slots
import InfoIcon from '@material-ui/icons/Info';
import Box from '@material-ui/core/Box';

import {makeStyles} from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
	infoContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		backgroundColor: '#f9f9f9',
		borderRadius: 8,
		padding: '12px 16px',
		border: '1px solid #e0e0e0',
		maxWidth: 500,
	},
	icon: {
		color: '#9cc54b',
		marginRight: 12,
		marginTop: 2,
		fontSize: 24,
	},
	text: {
		color: '#484747',
		fontSize: 14,
		lineHeight: '1.6',
	},
}));


const AvailableDeliverySlotCheckoutPage = (props) => {
	const classes = useStyles();
	// State variables
	const [list, setList] = useState([]);
	const [selectedDateIndex, setSelectedDateIndex] = useState(0);
	const [currentDaySlots, setCurrentDaySlots] = useState({hourly: [], medium: [], large: []});
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');
	const scrollContainerRef = useRef(null);

	// Mock props for standalone execution
	const {
		onSlotSelected,
		selectedSlot = null,
		pageName = "checkout",
		removeProduct = false,
		setRemoveProduct = b => {
		},
		grossTotal = 100,
		externalCourier,
		setExternalCourier
	} = props;


	const fetchSlots = async () => {
		setLoading(true);
		setErrorMessage('');

		try {
			const res = await API.getAllSlotsDelivery({orderType: 0, addressId: props.selectedAddress});
			const response = await res.json();

			const slotData = response.data;
			setExternalCourier(response.external_courier);
			setList(slotData);
			setLoading(false);
			props.setDeliverySlots(slotData);
			if (slotData && slotData.length > 0) {
				// Find index of today or the first available slot
				const todayIndex = slotData.findIndex(day => moment(day.date).isSame(moment(), 'day'));
				const initialIndex = todayIndex !== -1 ? todayIndex : 0;
				setSelectedDateIndex(initialIndex);
				setCurrentDaySlots(slotData[initialIndex].slots);
			} else {
				setCurrentDaySlots({hourly: [], medium: [], large: []});
				setErrorMessage('No delivery slots available.');
			}
		} catch (error) {
			console.error("Error fetching slot dates:", error);
			setLoading(false);
			setErrorMessage('Failed to load delivery slots. Please try again.');
		}
	};


	useEffect(() => {
		fetchSlots().then(r => {});
	}, [props.selectedAddress]);

	useEffect(() => {
		if (removeProduct) {
			fetchSlots().then(r => {
			});
			setRemoveProduct(false);
		}
	}, [removeProduct, grossTotal]);

	const handleClickDate = (index) => {
		if (index < 0 || index >= list.length) {
			console.warn('Invalid date index:', index);
			return;
		}
		setSelectedDateIndex(index);

		const slots = list[index]?.slots || {hourly: [], medium: [], large: []};
		setCurrentDaySlots({
			hourly: [...(slots.hourly || [])],
			medium: [...(slots.medium || [])],
			large: [...(slots.large || [])],
		});
	};


	// Custom navigation logic for date carousel
	const scroll = (direction) => {
		if (scrollContainerRef.current) {
			const scrollAmount = 150; // Adjust based on the width of your date buttons
			if (direction === 'left') {
				scrollContainerRef.current.scrollBy({left: -scrollAmount, behavior: 'smooth'});
			} else {
				scrollContainerRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'});
			}
		}
	};

	// Check if the scroll container is actually scrollable
	const [isScrollable, setIsScrollable] = useState(false);
	useEffect(() => {
		const checkScrollability = () => {
			if (scrollContainerRef.current) {
				setIsScrollable(scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth);
			}
		};
		// Use a small delay to ensure rendering is complete before checking scrollWidth
		const handler = setTimeout(checkScrollability, 100);
		window.addEventListener('resize', checkScrollability);
		return () => {
			clearTimeout(handler);
			window.removeEventListener('resize', checkScrollability);
		};
	}, [list, selectedDateIndex]); // Re-check when list or selectedDateIndex changes


	return (
		<PageContainer>
			{loading ? (
				<LoadingContainer>
					<Spinner animation="border" style={{color: `${props.cartType === "express" ? "#e83e8c" : "#9cc54b"}`}}/>
					<span className="ml-2 text-secondary">{t("loading_slots")}</span>
				</LoadingContainer>
			) : errorMessage ? (
				<StyledAlert variant="danger">{errorMessage}</StyledAlert>
			) : (
				externalCourier ? <>
					{/* Date Navigation */}
					{list.length > 0 ? (
						<DateNavWrapper>
							<NavArrowButton onClick={() => scroll('left')} disabled={!isScrollable} style={{marginLeft: '-54px'}}>
								<ArrowBackIosIcon style={{fontSize: 20, color: '#9cc54b'}}/>
							</NavArrowButton>

							<DateNavScrollContainer ref={scrollContainerRef}>
								{list.map((dayData, index) => (
									<StyledNavLinkButton
										key={dayData.date} // Use a unique key, like the date string
										onClick={() => handleClickDate(index)}
										className={index === selectedDateIndex ? 'active' : ''}
									>
										<div className="small font-weight-bold">
											{dayData.day_label}
										</div>
										<div className="small">{dayData.date_label}</div>
									</StyledNavLinkButton>
								))}
							</DateNavScrollContainer>

							<NavArrowButton onClick={() => scroll('right')} disabled={!isScrollable} style={{marginRight: '-54px'}}>
								<ArrowForwardIosIcon style={{fontSize: 20, color: '#9cc54b'}}/>
							</NavArrowButton>
						</DateNavWrapper>
					) : null}

					{/* Time Slots Content */}
					<SlotsGridRow>
						{/*Hourly Slots Column*/}
						<SlotColumn>
							<SlotsInnerContainer>
								{currentDaySlots.hourly && currentDaySlots.hourly.length > 0 ? (
									currentDaySlots.hourly.map((slot) => (
										<SlotButton
											key={`hourly-${list[selectedDateIndex]?.date}-${slot.slot_id}`}
											slot={slot}
											onSlotSelected={onSlotSelected}
											selectedSlotId={selectedSlot}
											pageName={pageName}
										/>
									))
								) : (
									<NoSlotsMessage>No hourly slots available.</NoSlotsMessage>
								)}
							</SlotsInnerContainer>
						</SlotColumn>

						{/* Medium Slots Column */}
						<SlotColumn>
							<SlotsInnerContainer>
								{currentDaySlots.medium && currentDaySlots.medium.length > 0 ? (
									currentDaySlots.medium.map((slot, index) => (
										<SlotButton
											key={`medium-${slot.slot_id || slot.label}-${index}`} // Use ID if available, fallback to label+index
											slot={slot}
											onSlotSelected={onSlotSelected}
											selectedSlotId={selectedSlot}
											pageName={pageName}
										/>
									))
								) : (
									<NoSlotsMessage>No slots available.</NoSlotsMessage>
								)}
							</SlotsInnerContainer>
						</SlotColumn>

						{/* Large Slot Column */}
						<SlotColumn>
							<SlotsInnerContainer>
								{currentDaySlots.large && currentDaySlots.large.length > 0 ? (
									currentDaySlots.large.map((slot, index) => (
										<SlotButton
											key={`large-${slot.slot_id || slot.label}-${index}`} // Use ID if available, fallback to label+index
											slot={slot}
											onSlotSelected={onSlotSelected}
											selectedSlotId={selectedSlot}
											pageName={pageName}
										/>
									))
								) : (
									<NoSlotsMessage>No full day slots available.</NoSlotsMessage>
								)}
							</SlotsInnerContainer>
						</SlotColumn>
					</SlotsGridRow>
				</> : <>
					<Row className="mb-3">
						{list.flatMap((slot) =>
								slot?.slots?.large?.length > 0 && slot?.slots?.large?.map((e, index) => (
									<Col xs={6} className="mb-3" key={`large-${e.slot_id || e.label}-${index}`}>
										<SlotButton
											slot={e}
											onSlotSelected={onSlotSelected}
											selectedSlotId={selectedSlot}
											pageName={pageName}
											externalCourier={externalCourier}
										/>
									</Col>
								))
						)}
					</Row>
					<Row>
						<Box className={classes.infoContainer}>
							<InfoIcon className={classes.icon}/>
							<Typography className={classes.text}>
								{t("province_delivery_info")}
							</Typography>
						</Box>
					</Row>
				</>

			)}
		</PageContainer>
	);
};

export default AvailableDeliverySlotCheckoutPage;
